'use client';

import { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { isKnownImageHost } from '@/lib/image-hosts';

interface BannerData {
  id: string;
  title: string;
  subtitle?: string | null;
  ctaText?: string | null;
  ctaUrl?: string | null;
  bgColor?: string | null;
  bgGradient?: string | null;
  imageUrl?: string | null;
  textColor: string;
  imageOnly?: boolean;
}

interface HeroBannerProps {
  banners: BannerData[];
  autoPlayMs?: number;
}

/**
 * Large hero-style banner carousel — used at LANDING_HERO.
 * Auto-advances every `autoPlayMs` ms (default 5 s).
 */
export function HeroBanner({ banners, autoPlayMs = 5000 }: HeroBannerProps) {
  const t = useTranslations('common');
  const [index, setIndex] = useState(0);

  const prev = useCallback(() => setIndex((i) => (i - 1 + banners.length) % banners.length), [banners.length]);
  const next = useCallback(() => setIndex((i) => (i + 1) % banners.length), [banners.length]);

  useEffect(() => {
    if (banners.length <= 1) return;
    const t = setInterval(next, autoPlayMs);
    return () => clearInterval(t);
  }, [next, autoPlayMs, banners.length]);

  if (banners.length === 0) return null;

  const banner = banners[index];

  // imageUrl now renders through next/image (below) so it can be transcoded/resized instead of
  // shipped as a raw CSS background. bgStyle only carries the gradient/color fallbacks.
  const bgStyle: React.CSSProperties = banner.imageUrl
    ? {}
    : banner.bgGradient
    ? { background: banner.bgGradient }
    : { backgroundColor: banner.bgColor ?? '#0f766e' };

  const color = banner.textColor ?? '#ffffff';

  return (
    <div className="relative w-full rounded-2xl overflow-hidden" style={{ ...bgStyle, minHeight: '220px' }}>
      {banner.imageUrl && (
        // This slot renders exactly one banner at a time (rotation swaps `banner`, it never
        // stacks multiple), so the mounted image IS the currently visible one — `priority` here
        // never preloads off-screen banners. `key={banner.id}` forces a fresh <Image> per
        // rotation instead of Next reusing the old <img> while the src swaps underneath it.
        // `imageUrl` comes from the admin media library, but a legacy row can still carry an
        // arbitrary URL typed before the picker existed — unlisted hosts throw at runtime, so
        // fall back to unoptimized rather than take the whole homepage hero down.
        <Image
          key={banner.id}
          src={banner.imageUrl}
          alt={banner.title}
          fill
          priority
          sizes="(min-width: 1152px) 1120px, calc(100vw - 32px)"
          className="object-cover object-center"
          unoptimized={!isKnownImageHost(banner.imageUrl)}
        />
      )}
      {banner.imageUrl && !banner.imageOnly && <div className="absolute inset-0 bg-black/35" />}

      {!banner.imageOnly && (
        <div className="relative z-10 flex flex-col items-center justify-center text-center px-6 py-14 md:py-20">
          <h2 className="text-2xl md:text-3xl font-extrabold leading-tight max-w-2xl" style={{ color }}>
            {banner.title}
          </h2>
          {banner.subtitle && (
            <p className="mt-3 text-base md:text-lg max-w-xl opacity-90" style={{ color }}>
              {banner.subtitle}
            </p>
          )}
          {banner.ctaText && banner.ctaUrl && (
            <Link
              href={banner.ctaUrl}
              className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold text-sm transition-all hover:opacity-90 active:scale-95"
              style={{ backgroundColor: color, color: banner.bgColor ?? '#0f766e' }}
            >
              {banner.ctaText}
            </Link>
          )}
        </div>
      )}

      {banners.length > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-3 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/20 hover:bg-black/40 transition-colors"
            aria-label={t('prevBanner')}
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          <button
            onClick={next}
            className="absolute right-3 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/20 hover:bg-black/40 transition-colors"
            aria-label={t('nextBanner')}
          >
            <ChevronRight className="w-5 h-5 text-white" />
          </button>

          {/* Dots */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5">
            {banners.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                className={`rounded-full transition-all ${i === index ? 'w-5 h-2 bg-white' : 'w-2 h-2 bg-white/50'}`}
                aria-label={t('goBanner', { n: i + 1 })}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
